import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Context Engine state types - mirrors backend domain processor outputs
 */

export interface Entity {
  name: string;
  type: string;
  mentions: number;
  confidence?: number;
  context?: string[];
}

export interface Task {
  id: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed';
  priority?: number;
  created_at: number;
  completed_at?: number;
  assigned_to?: string;
}

export interface Question {
  id: string;
  question: string;
  answer?: string;
  status: 'unanswered' | 'answered';
  created_at: number;
  answered_at?: number;
  confidence?: number;
}

export interface Document {
  id: string;
  title: string;
  url?: string;
  relevance_score: number;
  mentioned_at: number;
  context?: string;
}

export interface Relationship {
  entity1: string;
  entity2: string;
  relationship_type: string;
  confidence?: number;
  context?: string;
}

export interface TimelineEvent {
  id: string;
  timestamp: number;
  description: string;
  event_type: string;
  participants?: string[];
  location?: string;
}

export interface PriorityItem {
  id: string;
  content: string;
  priority: number;
  category?: string;
  created_at: number;
  locked_until?: number;
}

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * Main Context Engine state interface
 */
export interface ContextEngineState {
  // Connection status
  connectionStatus: ConnectionStatus;
  lastSync: number | null;

  // Domain processor data
  entities: Entity[];
  tasks: Task[];
  questions: Question[];
  documents: Document[];
  relationships: Relationship[];
  timeline: TimelineEvent[];
  prioritization: PriorityItem[];

  // Metadata
  callId: string | null;
  sessionStarted: number | null;
}

const initialState: ContextEngineState = {
  connectionStatus: 'disconnected',
  lastSync: null,
  entities: [],
  tasks: [],
  questions: [],
  documents: [],
  relationships: [],
  timeline: [],
  prioritization: [],
  callId: null,
  sessionStarted: null,
};

/**
 * Context Engine Redux slice
 *
 * Manages state from the AI Context Engine service, including:
 * - WebSocket connection status
 * - Real-time updates from 8 domain processors
 * - Session metadata
 */
const contextSlice = createSlice({
  name: 'context',
  initialState,
  reducers: {
    /**
     * Set WebSocket connection status
     */
    setConnectionStatus: (state, action: PayloadAction<ConnectionStatus>) => {
      state.connectionStatus = action.payload;
      if (action.payload === 'connected') {
        state.lastSync = Date.now();
      }
    },

    /**
     * Set call ID for the session
     */
    setCallId: (state, action: PayloadAction<string>) => {
      state.callId = action.payload;
      state.sessionStarted = Date.now();
    },

    /**
     * Update entities from Context Engine
     */
    updateEntities: (state, action: PayloadAction<Entity[]>) => {
      state.entities = action.payload;
      state.lastSync = Date.now();
    },

    /**
     * Add or update a single entity
     */
    upsertEntity: (state, action: PayloadAction<Entity>) => {
      const existingIndex = state.entities.findIndex(
        (e) => e.name === action.payload.name && e.type === action.payload.type
      );

      if (existingIndex !== -1) {
        state.entities[existingIndex] = action.payload;
      } else {
        state.entities.push(action.payload);
      }
      state.lastSync = Date.now();
    },

    /**
     * Update tasks from Context Engine
     */
    updateTasks: (state, action: PayloadAction<Task[]>) => {
      state.tasks = action.payload;
      state.lastSync = Date.now();
    },

    /**
     * Add or update a single task
     */
    upsertTask: (state, action: PayloadAction<Task>) => {
      const existingIndex = state.tasks.findIndex((t) => t.id === action.payload.id);

      if (existingIndex !== -1) {
        state.tasks[existingIndex] = action.payload;
      } else {
        state.tasks.push(action.payload);
      }
      state.lastSync = Date.now();
    },

    /**
     * Update questions from Context Engine
     */
    updateQuestions: (state, action: PayloadAction<Question[]>) => {
      state.questions = action.payload;
      state.lastSync = Date.now();
    },

    /**
     * Add or update a single question
     */
    upsertQuestion: (state, action: PayloadAction<Question>) => {
      const existingIndex = state.questions.findIndex((q) => q.id === action.payload.id);

      if (existingIndex !== -1) {
        state.questions[existingIndex] = action.payload;
      } else {
        state.questions.push(action.payload);
      }
      state.lastSync = Date.now();
    },

    /**
     * Update documents from Context Engine
     */
    updateDocuments: (state, action: PayloadAction<Document[]>) => {
      state.documents = action.payload;
      state.lastSync = Date.now();
    },

    /**
     * Add or update a single document
     */
    upsertDocument: (state, action: PayloadAction<Document>) => {
      const existingIndex = state.documents.findIndex((d) => d.id === action.payload.id);

      if (existingIndex !== -1) {
        state.documents[existingIndex] = action.payload;
      } else {
        state.documents.push(action.payload);
      }
      state.lastSync = Date.now();
    },

    /**
     * Update relationships from Context Engine
     */
    updateRelationships: (state, action: PayloadAction<Relationship[]>) => {
      state.relationships = action.payload;
      state.lastSync = Date.now();
    },

    /**
     * Add a new relationship
     */
    addRelationship: (state, action: PayloadAction<Relationship>) => {
      const { entity1, entity2 } = action.payload;
      // Check both directions, ignore type — matches DB unique constraint
      const exists = state.relationships.some(
        (r) =>
          (r.entity1 === entity1 && r.entity2 === entity2) ||
          (r.entity1 === entity2 && r.entity2 === entity1)
      );

      if (!exists) {
        state.relationships.push(action.payload);
        state.lastSync = Date.now();
      }
    },

    /**
     * Update timeline from Context Engine
     */
    updateTimeline: (state, action: PayloadAction<TimelineEvent[]>) => {
      // Sort timeline events by timestamp (newest first)
      state.timeline = action.payload.sort((a, b) => b.timestamp - a.timestamp);
      state.lastSync = Date.now();
    },

    /**
     * Add a timeline event
     */
    addTimelineEvent: (state, action: PayloadAction<TimelineEvent>) => {
      state.timeline.push(action.payload);
      // Re-sort after adding
      state.timeline.sort((a, b) => b.timestamp - a.timestamp);
      state.lastSync = Date.now();
    },

    /**
     * Update prioritization from Context Engine
     */
    updatePrioritization: (state, action: PayloadAction<PriorityItem[]>) => {
      // Sort by priority (highest first)
      state.prioritization = action.payload.sort((a, b) => b.priority - a.priority);
      state.lastSync = Date.now();
    },

    /**
     * Add or update a priority item
     */
    upsertPriorityItem: (state, action: PayloadAction<PriorityItem>) => {
      const existingIndex = state.prioritization.findIndex((p) => p.id === action.payload.id);

      if (existingIndex !== -1) {
        state.prioritization[existingIndex] = action.payload;
      } else {
        state.prioritization.push(action.payload);
      }

      // Re-sort after upsert
      state.prioritization.sort((a, b) => b.priority - a.priority);
      state.lastSync = Date.now();
    },

    /**
     * Reset all Context Engine state
     */
    resetContextEngine: (state) => {
      return initialState;
    },

    /**
     * Clear domain-specific data (keep connection info)
     */
    clearDomainData: (state) => {
      state.entities = [];
      state.tasks = [];
      state.questions = [];
      state.documents = [];
      state.relationships = [];
      state.timeline = [];
      state.prioritization = [];
      state.lastSync = null;
    },
  },
});

// Export actions
export const {
  setConnectionStatus,
  setCallId,
  updateEntities,
  upsertEntity,
  updateTasks,
  upsertTask,
  updateQuestions,
  upsertQuestion,
  updateDocuments,
  upsertDocument,
  updateRelationships,
  addRelationship,
  updateTimeline,
  addTimelineEvent,
  updatePrioritization,
  upsertPriorityItem,
  resetContextEngine,
  clearDomainData,
} = contextSlice.actions;

// Export reducer
export default contextSlice.reducer;

// Selectors
export const selectContextEngineStatus = (state: { context: ContextEngineState }) =>
  state.context.connectionStatus;

export const selectIsConnected = (state: { context: ContextEngineState }) =>
  state.context.connectionStatus === 'connected';

export const selectEntities = (state: { context: ContextEngineState }) => state.context.entities;

export const selectTasks = (state: { context: ContextEngineState }) => state.context.tasks;

export const selectPendingTasks = (state: { context: ContextEngineState }) =>
  state.context.tasks.filter((t) => t.status === 'pending');

export const selectCompletedTasks = (state: { context: ContextEngineState }) =>
  state.context.tasks.filter((t) => t.status === 'completed');

export const selectQuestions = (state: { context: ContextEngineState }) => state.context.questions;

export const selectUnansweredQuestions = (state: { context: ContextEngineState }) =>
  state.context.questions.filter((q) => q.status === 'unanswered');

export const selectDocuments = (state: { context: ContextEngineState }) => state.context.documents;

export const selectRelationships = (state: { context: ContextEngineState }) =>
  state.context.relationships;

export const selectTimeline = (state: { context: ContextEngineState }) => state.context.timeline;

export const selectPrioritization = (state: { context: ContextEngineState }) =>
  state.context.prioritization;

export const selectTopPriorities = (state: { context: ContextEngineState }, count: number = 5) =>
  state.context.prioritization.slice(0, count);

export const selectLastSync = (state: { context: ContextEngineState }) => state.context.lastSync;
